import { Color } from "@repo/app-types/constants/colors";
import {
  buildOutcomeGreenRateLabel,
  getOutcomeNoun,
  getOutcomePluralNoun,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type {
  ResolvedTeamPerformancePageRow,
  TeamBenchmarkScores,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  vocalIntelligenceDimensions,
  type VocalIntelligence,
} from "@repo/app-types/entities/vocalIntelligence";
import { formatCallLengthHoursMinutes } from "@repo/app-types/helpers/formatCallLength";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import {
  getVocalScoreColorTier,
  mapVocalScoreToTenPointScale,
  vocalIntelligenceDimensionLabels,
} from "@repo/ui/Charts/vocalIntelligenceRadarChart";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { getPillColor, Pill } from "@repo/ui/Pills/pill";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import { SimplePopover } from "@repo/ui/popover/simplePopover";
import { popoverOffsetForArrow } from "@repo/ui/popover/style";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import {
  Button as AriaButton,
  DialogTrigger,
  Heading,
} from "react-aria-components";
import { customerExperienceScoreToPercent } from "../topics/topicStatsFormatters.js";
import { CustomerExperienceScorePercentPill } from "./customerExperienceScorePercentPill.js";
import {
  buildGapBarTooltipContent,
  GapBar,
  GapBarColorScheme,
} from "./gapBar.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";
import { GradingTalkRatioPill } from "./gradingTalkRatioPill.js";
import { OutcomeRatePill } from "./outcomeRatePill.js";
import {
  buildOutcomeMultiLineData,
  type OutcomeValueRateSparklines,
} from "./outcomeTrendChartData.js";
import { TeammateCallCount } from "./teammateCallCount.js";
import { TrendChart, type LineData } from "./trendChart.js";

interface TeammateStatsTableProps {
  teammate:
    | (ResolvedTeamPerformancePageRow & {
        benchmarkScores?: TeamBenchmarkScores;
      })
    | undefined;
  isLoadingTeammate: boolean;
  outcomeDefinition: OutcomeDefinition | undefined;
  /**
   * Win rate (0-1) for the outcome. `undefined` when there's no outcome data
   * in the period (renders `--`); `null` when the outcome is inapplicable
   * (renders "N/A").
   */
  outcomeGreenRate: number | null | undefined;
  /** Per-outcome-value daily rate sparklines, keyed by value id. */
  outcomeValueRateSparklines: OutcomeValueRateSparklines | null | undefined;
  isLoadingOutcomeScore: boolean;
  dateRangeEnd: DateTime | undefined;
  /** Whether the CX score row should be shown (feature flag gated by the page). */
  customerExperienceScoreEnabled: boolean;
  /** Pre-gated vocal intelligence analysis for this playbook, or null when unavailable. */
  vocalScore: VocalIntelligence | null;
}

/** A muted em-dash placeholder for cells with no data. */
const emptyCell = <span className="text-muted-foreground">--</span>;

/**
 * The top-of-page teammate performance metrics, rendered as a metric table
 * (Metric / You / vs team). Available trends are progressively disclosed from
 * the teammate's value rather than occupying a dedicated column.
 */
export const TeammateStatsTable: FC<TeammateStatsTableProps> = (props) => {
  const {
    teammate,
    isLoadingTeammate,
    outcomeDefinition,
    outcomeGreenRate,
    outcomeValueRateSparklines,
    isLoadingOutcomeScore,
    dateRangeEnd,
    customerExperienceScoreEnabled,
    vocalScore,
  } = props;
  const scores = teammate?.scores;
  const benchmark = teammate?.benchmarkScores;

  const rows: StatRowData[] = [];

  // Playbook adherence
  rows.push({
    id: "playbook-adherence",
    label: "Playbook score",
    description:
      "How often the teammate met the playbook criteria on analyzed calls.",
    you: (
      <GradingPassRatePill
        size={Size.SMALL}
        passRate={scores?.pass_rate}
        isLoading={isLoadingTeammate}
      />
    ),
    teamComparison: renderPercentTeamComparison(
      scores?.pass_rate,
      benchmark?.median_pass_rate,
      `${Math.round((scores?.pass_rate ?? 0) * 100)}% playbook adherence`,
    ),
    trend:
      isNotNullNotUndefined(scores?.pass_rate) &&
      isNotNullNotUndefined(scores?.pass_rate_sparkline) ? (
        <TrendCell
          lines={[
            {
              key: "playbookScore",
              data: scores.pass_rate_sparkline,
            },
          ]}
          renderValue={(value) => `${Math.round(value * 100)}%`}
          minYRange={0.1}
          dateRangeEnd={dateRangeEnd}
        />
      ) : null,
  });

  // Outcome green rate
  if (outcomeDefinition) {
    const greenRate = outcomeGreenRate ?? undefined;
    const multiLineData = buildOutcomeMultiLineData(
      outcomeValueRateSparklines,
      outcomeDefinition,
    );
    const greenRateLabel = buildOutcomeGreenRateLabel(outcomeDefinition);

    rows.push({
      id: "outcome-green-rate",
      label: greenRateLabel,
      description: buildOutcomeRateDefinition(outcomeDefinition),
      you: (
        <OutcomeRatePill
          size={Size.SMALL}
          outcomeRate={outcomeGreenRate}
          isLoading={isLoadingOutcomeScore}
        />
      ),
      teamComparison: renderPercentTeamComparison(
        greenRate,
        benchmark?.median_outcome_green_rate,
        `${Math.round((greenRate ?? 0) * 100)}% ${greenRateLabel.toLowerCase()}`,
      ),
      trend:
        multiLineData.length > 0 ? (
          <TrendCell
            lines={multiLineData}
            renderValue={(value) => `${Math.round(value * 100)}%`}
            minYRange={0.1}
            dateRangeEnd={dateRangeEnd}
          />
        ) : null,
    });

    // Outcome green count
    const greenCount = scores?.outcome_green_count;
    const greenCountLabel = capitalize(getOutcomePluralNoun(outcomeDefinition));
    const greenCountDaily = scores?.outcome_green_count_sparkline;

    rows.push({
      id: "outcome-green-count",
      label: greenCountLabel,
      description: buildOutcomeCountDefinition(outcomeDefinition),
      you: isLoadingTeammate ? (
        <PillSkeleton size={Size.SMALL} />
      ) : greenCount !== undefined ? (
        <Pill
          size={Size.SMALL}
          color={Color.Gray}
          label={greenCount.toLocaleString()}
        />
      ) : (
        emptyCell
      ),
      teamComparison: renderCountTeamComparison(
        greenCount,
        benchmark?.median_outcome_green_count,
        greenCountLabel.toLowerCase(),
      ),
      trend: greenCountDaily ? (
        <TrendCell
          lines={[{ key: "outcomeCount", data: greenCountDaily }]}
          renderValue={(value) => value.toLocaleString()}
          minYRange={5}
          dateRangeEnd={dateRangeEnd}
          getColorClassName={() => "text-muted-foreground"}
        />
      ) : null,
    });

    // Avg time to outcome. Revenue playbooks get the dollar-denominated
    // per-hour metric instead, so this row is omitted there — matching the
    // teammates list column.
    if (!isRevenueOutcomeDefinition(outcomeDefinition)) {
      const outcomeNoun = getOutcomeNoun(outcomeDefinition);
      const avgSeconds = scores?.seconds_per_green_outcome;
      const hasAvgTime = avgSeconds !== undefined && avgSeconds > 0;
      const formattedAvgTime = hasAvgTime
        ? formatCallLengthHoursMinutes(avgSeconds)
        : undefined;
      const medianSeconds = benchmark?.median_seconds_per_green_outcome;
      const avgTimeTrend = scores?.seconds_per_green_outcome_sparkline;

      rows.push({
        id: "avg-time-to-outcome",
        label: `Avg time to ${outcomeNoun}`,
        description: `Call time on all calls with an outcome (excluding N/A), divided by the number of ${getOutcomePluralNoun(outcomeDefinition)}.`,
        you: isLoadingTeammate ? (
          <PillSkeleton size={Size.SMALL} />
        ) : hasAvgTime ? (
          <Pill size={Size.SMALL} color={Color.Gray} label={formattedAvgTime} />
        ) : (
          emptyCell
        ),
        teamComparison:
          hasAvgTime &&
          medianSeconds !== undefined &&
          medianSeconds > 0 &&
          formattedAvgTime !== undefined
            ? buildTeamComparison({
                tooltipContentPrefix: `${formattedAvgTime} avg time to ${outcomeNoun}`,
                scoreValue: avgSeconds,
                benchmarkValue: medianSeconds,
                options: {
                  range: [
                    0,
                    Math.max(60, Math.max(avgSeconds, medianSeconds) * 2),
                  ],
                  lowerIsBetter: true,
                  deltaDecimalPlaces: 0,
                  formatDelta: formatCallLengthHoursMinutes,
                },
              })
            : null,
        trend:
          hasAvgTime && isNotNullNotUndefined(avgTimeTrend) ? (
            <TrendCell
              lines={[
                {
                  key: "avgTimeToOutcome",
                  data: avgTimeTrend,
                },
              ]}
              renderValue={formatCallLengthHoursMinutes}
              minYRange={60}
              dateRangeEnd={dateRangeEnd}
              getColorClassName={() => "text-muted-foreground"}
            />
          ) : null,
      });
    }
  }

  // CX score
  if (customerExperienceScoreEnabled) {
    const cxScore = scores?.customer_experience_score;
    const cxTrend = scores?.customer_experience_score_sparkline;
    const medianCx = benchmark?.median_customer_experience_score;

    if (cxScore !== undefined || isLoadingTeammate) {
      rows.push({
        id: "cx-score",
        label: "CX score",
        description:
          "How customers felt by the end of their calls, scored 0-100 and averaged across analyzed calls.",
        you: isLoadingTeammate ? (
          <PillSkeleton size={Size.SMALL} />
        ) : (
          <CustomerExperienceScorePercentPill
            size={Size.SMALL}
            score={cxScore}
          />
        ),
        teamComparison:
          cxScore !== undefined && medianCx !== undefined
            ? buildTeamComparison({
                tooltipContentPrefix: `${Math.round(customerExperienceScoreToPercent(cxScore))} CX score`,
                scoreValue: customerExperienceScoreToPercent(cxScore),
                benchmarkValue: customerExperienceScoreToPercent(medianCx),
              })
            : null,
        trend:
          isNotNullNotUndefined(cxScore) && isNotNullNotUndefined(cxTrend) ? (
            <TrendCell
              lines={[
                {
                  key: "cxScore",
                  data: cxTrend.map((value) =>
                    value === null
                      ? value
                      : customerExperienceScoreToPercent(value),
                  ),
                },
              ]}
              renderValue={(value) => `${Math.round(value)}`}
              minYRange={10}
              dateRangeEnd={dateRangeEnd}
            />
          ) : null,
      });
    }
  }

  // Call volume
  const recordingCountDaily = teammate?.recording_count_sparkline;
  rows.push({
    id: "call-volume",
    label: "Call volume",
    description: "Number of recorded calls analyzed for this teammate.",
    you: isLoadingTeammate ? (
      <PillSkeleton size={Size.SMALL} />
    ) : teammate ? (
      <Pill
        size={Size.SMALL}
        color={Color.Gray}
        label={<TeammateCallCount row={teammate} />}
      />
    ) : (
      emptyCell
    ),
    teamComparison: renderCountTeamComparison(
      teammate?.recording_count,
      benchmark?.median_recording_count,
      "calls",
    ),
    trend: recordingCountDaily ? (
      <TrendCell
        lines={[{ key: "callVolume", data: recordingCountDaily }]}
        renderValue={(value) => value.toLocaleString()}
        minYRange={5}
        dateRangeEnd={dateRangeEnd}
        getColorClassName={() => "text-muted-foreground"}
      />
    ) : null,
  });

  // Talk ratio
  rows.push({
    id: "talk-ratio",
    label: "Talk ratio",
    description: "Percent of total speaking time that came from the teammate.",
    you: (
      <GradingTalkRatioPill
        size={Size.SMALL}
        talkRatio={scores?.talk_ratio}
        isLoading={isLoadingTeammate}
      />
    ),
    teamComparison: renderPercentTeamComparison(
      scores?.talk_ratio,
      benchmark?.median_talk_ratio,
      `Rep talking ${Math.round((scores?.talk_ratio ?? 0) * 100)}% of the time`,
      { colorScheme: GapBarColorScheme.Neutral },
    ),
    trend:
      isNotNullNotUndefined(scores?.talk_ratio) &&
      isNotNullNotUndefined(scores?.talk_ratio_sparkline) ? (
        <TrendCell
          lines={[
            {
              key: "talkRatio",
              data: scores.talk_ratio_sparkline,
            },
          ]}
          renderValue={(value) => `${Math.round(value * 100)}%`}
          minYRange={0.1}
          dateRangeEnd={dateRangeEnd}
          getColorClassName={() => "text-muted-foreground"}
        />
      ) : null,
  });

  return (
    <div className="grid gap-y-4 self-start">
      <div className="grid grid-cols-[minmax(9rem,1fr)_max-content_max-content] overflow-hidden rounded-lg border border-border-light bg-white shadow-xs">
        <div className="col-span-full grid grid-cols-subgrid items-center gap-x-5 border-b border-border-light px-3 py-2 text-xs font-semibold text-foreground sm:px-4">
          <div>Metric</div>
          <div className="justify-self-end">You</div>
          <div className="justify-self-end">vs team</div>
        </div>

        {rows.map(({ id, ...row }) => (
          <StatRow key={id} {...row} />
        ))}
      </div>

      {vocalScore ? (
        <div className="overflow-hidden rounded-lg border border-border-light bg-white shadow-xs">
          <VocalIntelligenceRow vocalScore={vocalScore} />
        </div>
      ) : null}
    </div>
  );
};

interface StatRowData {
  id: string;
  label: ReactNode;
  /** One-line explanation shown in an info tooltip after the metric name. */
  description?: ReactNode;
  you: ReactNode;
  teamComparison: TeamComparison | null;
  trend: ReactNode;
}

interface StatRowProps extends Omit<StatRowData, "id"> {}

const StatRow: FC<StatRowProps> = (props) => {
  const { label, description, you, teamComparison, trend } = props;

  return (
    <div className="group/metric-row col-span-full grid grid-cols-subgrid items-center gap-x-5 gap-y-2 border-b border-border-light px-3 py-3 last:border-b-0 sm:px-4">
      <MetricLabel label={label} description={description} />
      <MetricValueWithTrend
        label={label}
        value={you}
        trend={trend}
        delta={teamComparison?.delta}
      />
      <div className="justify-self-end">
        {teamComparison?.visualization ?? emptyCell}
      </div>
    </div>
  );
};

interface MetricValueWithTrendProps {
  label: ReactNode;
  value: ReactNode;
  trend: ReactNode;
  delta?: ReactNode;
}

const MetricValueWithTrend: FC<MetricValueWithTrendProps> = (props) => {
  const { label, value, trend, delta } = props;

  if (!trend) {
    return <div className="justify-self-end">{value}</div>;
  }

  const metricName = typeof label === "string" ? label : "Metric";

  return (
    <DialogTrigger>
      <AriaButton
        aria-label={`Show ${metricName} trend`}
        className="w-fit justify-self-end rounded-md text-left outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {value}
      </AriaButton>
      <SimplePopover
        aria-label={`${metricName} trend`}
        placement="end"
        shouldFlip
        isNonModal
        offset={popoverOffsetForArrow}
      >
        <div className="w-72">
          <div className="mb-2 flex items-center justify-between gap-3">
            <Heading
              level={3}
              className="text-xs font-semibold text-foreground"
            >
              Trend
            </Heading>
            {delta ? (
              <div className="shrink-0">
                <span className="sr-only">Compared with team: </span>
                {delta}
              </div>
            ) : null}
          </div>
          {trend}
        </div>
      </SimplePopover>
    </DialogTrigger>
  );
};

interface MetricLabelProps {
  label: ReactNode;
  description?: ReactNode;
}

/** A truncating metric name followed by an optional info tooltip. */
const MetricLabel: FC<MetricLabelProps> = (props) => {
  const { label, description } = props;

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Heading
        level={2}
        className="truncate text-sm font-medium text-foreground"
      >
        {label}
      </Heading>
      {description ? (
        <InfoIconTooltip className="opacity-0 transition-opacity group-focus-within/metric-row:opacity-100 group-hover/metric-row:opacity-100">
          {description}
        </InfoIconTooltip>
      ) : null}
    </div>
  );
};

interface TrendCellProps {
  lines: readonly LineData[];
  renderValue: (value: number) => string;
  minYRange: number;
  dateRangeEnd: DateTime | undefined;
  getColorClassName?: (deltaY: number) => string;
}

const TrendCell: FC<TrendCellProps> = (props) => {
  const { lines, renderValue, minYRange, dateRangeEnd, getColorClassName } =
    props;

  return (
    <TrendChart
      className="h-24 w-full"
      minYRange={minYRange}
      lines={lines}
      renderValue={renderValue}
      getColorClassName={getColorClassName}
      showsXAxisLabels
      xAxisLabels={buildXAxisLabels(lines[0]?.data ?? [], dateRangeEnd)}
    />
  );
};

/**
 * Overall + per-dimension vocal intelligence scores. This uses an independent
 * responsive grid so its wider dimension labels cannot influence the main
 * performance table's content-sized numeric columns.
 */
interface VocalIntelligenceRowProps {
  vocalScore: VocalIntelligence;
}

const VocalIntelligenceRow: FC<VocalIntelligenceRowProps> = (props) => {
  const { vocalScore } = props;
  const overallTenPoint = mapVocalScoreToTenPointScale(
    (vocalScore.authority.score +
      vocalScore.energy.score +
      vocalScore.rapport.score) /
      3,
  );

  return (
    <div className="group/metric-row grid gap-y-3 px-4 py-4 md:max-[1200px]:grid-cols-[minmax(9rem,1fr)_minmax(0,24rem)] md:max-[1200px]:items-center md:max-[1200px]:gap-x-5 md:max-[1200px]:gap-y-0">
      <MetricLabel
        label="Vocal intelligence"
        description="An overall score for vocal qualities like confidence, energy, and rapport, calculated each week from a sample of the teammate's calls."
      />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 min-[480px]:grid-cols-4 min-[480px]:gap-x-2 md:max-[1200px]:justify-self-end">
        <div className="flex min-w-0 flex-col items-start gap-1.5">
          <span className="text-xs font-medium text-foreground">Overall</span>
          <VocalScorePill score={overallTenPoint} size={Size.SMALL} />
        </div>

        {vocalIntelligenceDimensions.map((dimension) => (
          <div
            key={dimension}
            className="flex min-w-0 flex-col items-start gap-1.5"
          >
            <span className="text-xs font-medium text-muted-foreground">
              {vocalIntelligenceDimensionLabels[dimension]}
            </span>
            <TooltipTrigger>
              <TooltipHoverTarget shape="rounded">
                <VocalScorePill
                  score={mapVocalScoreToTenPointScale(
                    vocalScore[dimension].score,
                  )}
                  size={Size.SMALL}
                />
              </TooltipHoverTarget>
              <SimpleTooltip>{vocalScore[dimension].commentary}</SimpleTooltip>
            </TooltipTrigger>
          </div>
        ))}
      </div>
    </div>
  );
};

interface VocalScorePillProps {
  score: number;
  size: Size;
}

const VocalScorePill: FC<VocalScorePillProps> = (props) => {
  const { score, size } = props;

  return (
    <Pill
      size={size}
      color={getPillColor(getVocalScoreColorTier(score))}
      label={formatTenPointScore(score)}
    />
  );
};

function formatTenPointScore(score: number): string {
  return score.toFixed(1).replace(/\.0$/, "");
}

function buildOutcomeRateDefinition(
  outcomeDefinition: OutcomeDefinition,
): string {
  const outcomeNoun = formatOutcomeNounForSentence(
    getOutcomeNoun(outcomeDefinition),
  );
  return `The percent of opportunities that resulted in ${outcomeNoun}. Opportunities marked N/A are not counted.`;
}

function buildOutcomeCountDefinition(
  outcomeDefinition: OutcomeDefinition,
): string {
  const outcomePluralNoun = getOutcomePluralNoun(outcomeDefinition)
    .trim()
    .toLowerCase();
  return `Total ${outcomePluralNoun || "outcomes"} in this period.`;
}

/** Lower-cases the noun and prefixes the correct indefinite article. */
function formatOutcomeNounForSentence(outcomeNoun: string): string {
  const trimmedNoun = outcomeNoun.trim();
  if (!trimmedNoun) {
    return "a target outcome";
  }

  const sentenceCaseNoun =
    trimmedNoun.charAt(0).toLowerCase() + trimmedNoun.slice(1);
  const article = /^[aeiou]/i.test(sentenceCaseNoun) ? "an" : "a";
  return `${article} ${sentenceCaseNoun}`;
}

interface GapBarTooltipOptions {
  benchmarkLabel?: string;
  range?: readonly [number, number];
  colorScheme?: GapBarColorScheme;
  lowerIsBetter?: boolean;
  deltaPrefix?: string;
  deltaDecimalPlaces?: number;
  formatDelta?: (absoluteDelta: number) => string;
}

interface TeamComparison {
  visualization: ReactNode;
  delta: ReactNode;
}

interface TeamComparisonArgs {
  tooltipContentPrefix: string;
  scoreValue: number;
  benchmarkValue: number;
  options?: GapBarTooltipOptions;
}

/**
 * A gap bar whose benchmark-comparison sentence is revealed in a tooltip below
 * the bar on hover.
 */
interface GapBarWithTooltipProps {
  tooltipContentPrefix: string;
  scoreValue: number;
  benchmarkValue: number;
  options?: GapBarTooltipOptions;
}

const GapBarWithTooltip: FC<GapBarWithTooltipProps> = (props) => {
  const { tooltipContentPrefix, scoreValue, benchmarkValue, options } = props;

  return (
    <TooltipTrigger>
      <TooltipHoverTarget>
        <GapBar
          value={scoreValue}
          benchmark={benchmarkValue}
          range={options?.range}
          size="sm"
          colorScheme={options?.colorScheme}
          lowerIsBetter={options?.lowerIsBetter}
          deltaPrefix={options?.deltaPrefix}
          deltaDecimalPlaces={options?.deltaDecimalPlaces}
          formatDelta={options?.formatDelta}
        />
      </TooltipHoverTarget>
      <SimpleTooltip>
        {buildGapBarTooltipContent(
          tooltipContentPrefix,
          scoreValue,
          benchmarkValue,
          options,
        )}
      </SimpleTooltip>
    </TooltipTrigger>
  );
};

function buildTeamComparison(props: TeamComparisonArgs): TeamComparison {
  const { tooltipContentPrefix, scoreValue, benchmarkValue, options } = props;

  return {
    visualization: (
      <GapBarWithTooltip
        tooltipContentPrefix={tooltipContentPrefix}
        scoreValue={scoreValue}
        benchmarkValue={benchmarkValue}
        options={options}
      />
    ),
    delta: (
      <GapBar
        value={scoreValue}
        benchmark={benchmarkValue}
        range={options?.range}
        size="sm"
        showBar={false}
        colorScheme={options?.colorScheme}
        lowerIsBetter={options?.lowerIsBetter}
        deltaPrefix={options?.deltaPrefix}
        deltaDecimalPlaces={options?.deltaDecimalPlaces}
        formatDelta={options?.formatDelta}
        deltaClassName="mb-0 text-[11px] tabular-nums"
      />
    ),
  };
}

function renderPercentTeamComparison(
  value: number | undefined,
  benchmark: number | undefined,
  tooltipPrefix: string,
  options?: { colorScheme?: GapBarColorScheme },
): TeamComparison | null {
  if (!isNotNullNotUndefined(value) || !isNotNullNotUndefined(benchmark)) {
    return null;
  }
  return buildTeamComparison({
    tooltipContentPrefix: tooltipPrefix,
    scoreValue: Math.round(value * 100),
    benchmarkValue: Math.round(benchmark * 100),
    options,
  });
}

function renderCountTeamComparison(
  value: number | undefined,
  benchmark: number | undefined,
  noun: string,
): TeamComparison | null {
  if (value === undefined || benchmark === undefined) {
    return null;
  }
  return buildTeamComparison({
    tooltipContentPrefix: `${value.toLocaleString()} ${noun}`,
    scoreValue: value,
    benchmarkValue: benchmark,
    options: {
      range: [0, Math.max(1, Math.max(value, benchmark) * 2)],
      deltaDecimalPlaces: 0,
      formatDelta: (delta) => delta.toLocaleString(),
    },
  });
}

/**
 * Anchors the sparkline's x-axis to the active date range: the right edge is
 * the range end, the left edge is the earliest day with data.
 */
function buildXAxisLabels(
  data: readonly (number | null | undefined)[],
  dateRangeEnd: DateTime | undefined,
): { start: string; end: string } | undefined {
  if (!dateRangeEnd) {
    return undefined;
  }
  const lastDataIndex = data.findLastIndex(isNotNullNotUndefined);
  return {
    start: dateRangeEnd
      .minus({ days: Math.max(0, lastDataIndex) })
      .toFormat("L/dd"),
    end: dateRangeEnd.toFormat("L/dd"),
  };
}
